/**
 * The one fetch wrapper.
 *
 * Every call goes through `/api/*`, which `actos-ui`'s Worker proxies to
 * `actos-api` server-side after resolving identity (§4.4). The browser holds a
 * session cookie and nothing else — never a provider token.
 *
 * Errors carry the API's stated `reason` where there is one. A UI that shows
 * "Request failed" when the server explained exactly what was wrong wastes the
 * explanation the server went to the trouble of writing.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /**
     * The whole response body, for the refusals that say more than a reason:
     * a 422 from the marketing Library carries `problems`, each with a line.
     */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The last body served for a GET, keyed by full path including query string.
 *
 * Kept here rather than left to the browser's HTTP cache because nothing enters
 * that cache without a `Cache-Control` the responses did not carry, and because
 * a 304 has to be turned back into the value the caller expects — `api()`
 * returns a parsed object, not a Response, so there is nowhere else to hold it.
 *
 * The key must include the query string: `/tasks?since=` changes at the rep's
 * local midnight and `/tasks/history?before=` differs per page, and a key that
 * ignored them would answer one question with another's cached answer.
 *
 * Bounded, because this is a long-lived single-page app: without a ceiling,
 * every distinct `?before=` cursor a rep pages through would be held for the
 * life of the tab. Oldest-first eviction is enough — the polled URLs are a
 * handful that are hit constantly, and they re-insert on every use.
 */
const CACHE_LIMIT = 32;
const cache = new Map<string, { etag: string | null; body: unknown }>();

/**
 * `etag` is nullable, and that is the whole point of this entry existing.
 *
 * Only four endpoints serve an ETag — `/tasks`, `/notices`, `/capacity` and
 * `/contact-lists`, the polled ones `lib/etag.ts` was written for. While a tag
 * was the price of admission, this cache held four paths and every other screen
 * in the app went to the network from nothing on every single visit.
 *
 * So the body is kept whether or not a tag came with it. The two jobs were
 * always separate and were only ever coupled by accident: the BODY is what lets
 * a revisit paint immediately (`peek`), and the TAG is what makes the
 * revalidation behind that paint cheap. A path with no tag still revalidates —
 * it just pays full freight for the answer, exactly as it did before, behind a
 * screen that is already drawn instead of in front of a blank one.
 */
function remember(key: string, etag: string | null, body: unknown): void {
  // Delete before set so a re-used key moves to the end of the insertion order.
  cache.delete(key);
  cache.set(key, { etag, body });
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
}

/**
 * The last body seen at this path, if this tab has ever seen one.
 *
 * For painting, never for deciding. A caller renders what this returns and then
 * calls `api()` for the same path regardless — `useQuery` does exactly that.
 * Nothing here says the value is current, only that it is what was true the
 * last time anyone looked, which is a far better first frame than "Loading…"
 * and a far worse basis for an action.
 *
 * A COPY, for the reason the 304 branch below gives at length: handing back the
 * same reference twice reads to React as "nothing happened", and everything
 * derived from a clock inside it silently stops moving.
 */
export function peek<T>(path: string): T | undefined {
  const cached = cache.get(path);
  return cached ? ({ ...(cached.body as object) } as T) : undefined;
}

/**
 * Forget every remembered response.
 *
 * Called on sign-out, and by the tests, which have no page reload to clear
 * module state between cases. Not load-bearing for correctness — an ETag is
 * scoped to the caller, so the server refuses to 304 one person's body to
 * another — but a shared machine should not keep one user's responses in memory
 * because the thing that makes it safe lives in a different file.
 */
export function clearApiCache(): void {
  cache.clear();
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  /*
   * Conditional requests, but only for reads. A GET is the only method whose
   * answer can be "the same as last time"; sending `If-None-Match` on a POST
   * would be asking a mutation to be skipped, which is a different and much
   * worse thing than a cache hit.
   */
  const method = (init?.method ?? 'GET').toUpperCase();
  const cacheable = method === 'GET';
  const cached = cacheable ? cache.get(path) : undefined;

  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      // Only when there is a tag to send. A cached body without one is a paint
      // aid, not a validator, and `If-None-Match: null` is not a thing to ask.
      ...(cached?.etag ? { 'if-none-match': cached.etag } : {}),
      ...(init?.headers ?? {}),
    },
  });

  /*
   * BEFORE the `!response.ok` test, deliberately. `Response.ok` is 200-299, so
   * a 304 would otherwise fall into the error branch, fail to parse its empty
   * body, and throw "Request failed (304)" — which in the workspace becomes a
   * red banner over a board that is perfectly current. The whole optimisation
   * would present as an outage.
   */
  if (response.status === 304) {
    /*
     * A COPY, never the cached object itself.
     *
     * Returning the same reference twice is indistinguishable from "nothing
     * happened" to React: `setSnapshot(sameObject)` bails out of the render,
     * and everything derived from the clock inside it stops moving. The queue
     * would go on saying "sends in 3h" an hour later, because the value never
     * changed even though what it renders to did. A shallow copy is enough —
     * the identity that matters is the top-level object every consumer holds.
     */
    if (cached) return { ...(cached.body as object) } as T;

    /*
     * A 304 with nothing to serve it from. Our own conditional header is only
     * sent when there IS a cached body, so reaching here means the browser
     * revalidated on its own behalf — `private, no-cache` invites exactly that
     * — and handed the bare 304 through rather than reconstructing the body.
     *
     * Ask again, bypassing the HTTP cache. Cheap because it cannot recur: the
     * answer to an unconditional request is never 304.
     */
    const fresh = await fetch(`/api${path}`, {
      ...init,
      cache: 'reload',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
    const freshBody = (await fresh.json().catch(() => ({}))) as Record<string, unknown>;
    if (!fresh.ok) {
      throw new ApiError(
        fresh.status,
        (freshBody.reason as string) ??
          (freshBody.error as string) ??
          `Request failed (${fresh.status})`,
        freshBody.error as string | undefined,
        freshBody,
      );
    }
    if (cacheable) remember(path, fresh.headers.get('etag'), freshBody);
    return freshBody as T;
  }

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (body.reason as string) ?? (body.error as string) ?? `Request failed (${response.status})`,
      body.error as string | undefined,
      body,
    );
  }

  if (cacheable) remember(path, response.headers.get('etag'), body);

  return body as T;
}

/**
 * Parse a timestamp from either shape the API produces.
 *
 * Drizzle's typed selects return `Date`s that serialise as ISO-8601 with a `T`.
 * Raw `db.execute` returns Postgres's own rendering — `2026-08-02 08:37:50+00`,
 * with a space. V8 accepts both; the ECMAScript spec only requires the first,
 * and JavaScriptCore has historically rejected the second. That would show as
 * "Invalid Date" in Safari on exactly the surfaces built on raw SQL.
 */
function parseStamp(value: string): number {
  const normalised = value
    // `2026-08-02 10:49:06+00` → `2026-08-02T10:49:06+00`
    .replace(' ', 'T')
    /**
     * …and then `+00` → `+00:00`.
     *
     * Postgres renders a whole-hour offset with two digits and no colon, which
     * ECMAScript does not accept — `new Date('2026-08-02T10:49:06+00')` is
     * Invalid Date in V8. Every timestamp from a raw `db.execute` therefore
     * rendered as "NaNd" in the Replies view. Handling the space alone was not
     * enough; the offset is the other half.
     */
    .replace(/([+-]\d{2})$/, '$1:00');

  return new Date(normalised).getTime();
}

/**
 * Has this timestamp passed? Through `parseStamp`, so the answer is right for
 * both timestamp shapes the API produces — a hand-rolled `new Date(iso)` is
 * exactly the Safari bug parseStamp exists to prevent.
 */
export function isPast(iso: string | null | undefined): boolean {
  if (!iso) return false;
  return parseStamp(iso) <= Date.now();
}

/**
 * Did this happen on the viewer's own calendar day?
 *
 * The rep's midnight, not the server's — the same definition `localMidnightIso`
 * sends with the workspace snapshot, so a "today" count and the board it sits
 * above can never disagree about where the day started.
 *
 * It lives here for the reason every other formatter does: through `parseStamp`,
 * so it handles both timestamp shapes the API produces. A caller reaching for
 * `new Date(iso)` gets the Postgres rendering wrong and silently counts nothing.
 */
export function isToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const ms = parseStamp(iso);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return ms >= start.getTime() && ms <= Date.now();
}

/** Compact relative time — "in 3h", "2d ago". Absolute dates read as noise in a queue. */
export function relative(iso: string | null | undefined): string {
  if (!iso) return '—';

  const delta = parseStamp(iso) - Date.now();
  const abs = Math.abs(delta);
  const suffix = delta < 0 ? ' ago' : '';
  const prefix = delta > 0 ? 'in ' : '';

  const unit =
    abs < 60_000
      ? 'less than a minute'
      : abs < 3_600_000
        ? `${Math.round(abs / 60_000)}m`
        : abs < 86_400_000
          ? `${Math.round(abs / 3_600_000)}h`
          : `${Math.round(abs / 86_400_000)}d`;

  return `${prefix}${unit}${suffix}`;
}

export function absolute(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(parseStamp(iso)).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The same instant, on someone else's clock — §5.2's sending window, said in
 * the terms that decide whether it is a good time to arrive.
 *
 * A send slot shown only in the rep's own timezone answers a question nobody
 * asked. "Sends at 2:30 PM" is fine if the prospect is down the corridor and
 * useless if they are in Sydney, where the same instant is half past seven the
 * next morning — and the whole point of picking the slot was landing inside
 * THEIR working day.
 *
 * Returns null rather than a fallback when the zone is unknown or invalid, so a
 * caller cannot accidentally label the viewer's own clock as the recipient's.
 * An invalid zone is caught rather than thrown: a bad country mapping must not
 * take a queue row down with it.
 */
export function absoluteInZone(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  if (!iso || !timeZone) return null;
  try {
    return new Date(parseStamp(iso)).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
  } catch {
    return null;
  }
}

/**
 * Date without the time, for columns where the hour is noise — when a
 * suppression was added, when a batch was imported.
 *
 * It exists so those columns cannot hand-roll the parse. The Blocklist did,
 * and got the half of it that `parseStamp` warns about above: it replaced the
 * space but not the `+00` offset, which is worse than doing nothing, because
 * the bare form parses under V8's lenient path and the half-converted form is
 * rejected by the strict ISO one. Every date on that page read "Invalid Date".
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(parseStamp(iso)).toLocaleDateString();
}
